import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
  AddressInput,
  CreateCancelButtons,
  DateCalendarForm,
  InputMain,
  NumericFormatCustom,
  PhoneInput2,
  SelectMain,
} from 'components';
import { RateTypesList, SexTypesList } from 'fragments';
import { FindError, FindLoad, FindSuccess, isEmptyObj, newDateWithoutUtc, renderPayerName, useModal } from 'utils';
import { httpRequestsOnErrorsActions, httpRequestsOnSuccessActions, patientsActions, payerActions } from 'store';
import { Svg } from 'assets';
import { PatientTypesList } from '../constants';

const ACTION_TYPE_PAYERS = 'GET_PAYERS';

export const AddPatient = ({ info, afterSuccess }) => {
  const payersList = useSelector((state) => state.payers?.payersList);
  const dispatch = useDispatch();
  const { handleSubmit, control, getValues, setValue, clearErrors, setError, reset } = useForm();
  const { close } = useModal();
  const ACTION_TYPE = info ? 'EDIT_PATIENT' : 'ADD_PATIENT';
  const loading = FindLoad(ACTION_TYPE);
  const success = FindSuccess(ACTION_TYPE);
  const successPayers = FindSuccess(ACTION_TYPE_PAYERS);
  const beckError = FindError(ACTION_TYPE);

  useEffect(() => {
    if (success?.type) {
      dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE));
      close();
      afterSuccess?.();
    }
  }, [success]);

  useEffect(() => {
    const params = {};
    dispatch(payerActions.getPayers(params));
  }, []);

  useEffect(() => {
    if (successPayers?.type) {
      dispatch(httpRequestsOnSuccessActions.removeSuccess(successPayers));
      if (info?.insurance?._id) {
        setValue(
          'insurance',
          payersList?.payers.find((payer) => payer?.id === info?.insurance?._id || payer?.id === info?.insurance?.id)
            ?.id,
        );
      }
    }
  }, [successPayers]);

  useEffect(() => {
    if (beckError?.error?.length) {
      if (beckError?.error?.includes('phone must be a valid phone number')) {
        setError('phone', {
          message: 'Phone must be a valid phone number',
        });
      }
    }
  }, [beckError?.error]);

  useEffect(() => {
    if (info) {
      reset({
        ...info,
      });
    }
  }, [info]);

  useEffect(() => {
    dispatch(httpRequestsOnErrorsActions.removeError(ACTION_TYPE));
    clearErrors();

    return () => {
      dispatch(httpRequestsOnErrorsActions.removeError(ACTION_TYPE));
      clearErrors();
    };
  }, []);

  const onSubmit = (data) => {
    const params = {
      ...data,
    };

    data?.los ? (params.los = data?.los) : delete params.los;
    data?.status ? (params.status = data?.status) : delete params.status;
    data?.sex ? (params.sex = data?.sex) : delete params.sex;
    data?.insurance ? (params.insurance = data?.insurance) : delete params.insurance;
    data?.dob ? (params.dob = newDateWithoutUtc(data?.dob)) : delete params.dob;

    if (isEmptyObj(params?.address)) {
      delete params?.address;
    }

    if (info) {
      dispatch(patientsActions.editPatient(info?.id, params));
    } else {
      dispatch(patientsActions.addPatient(params));
    }
  };
  return (
    <form className="add-patient-modal-wrapper" onSubmit={handleSubmit(onSubmit)}>
      <div className="modal-header-main">
        <h3 className="modal-header-title">{info ? 'Edit' : 'Add'} Patient</h3>

        <button className="modal-close-btn-main" type="button" onClick={close}>
          <Svg.CloseSvg />
        </button>
      </div>

      <div className="modal-body-main">
        <div className="flex-gap-16">
          <div style={{ width: '100%' }}>
            <InputMain
              name="lastName"
              labelHeader="Last Name"
              placeholder="Ex. Smith"
              control={control}
              rules={{
                required: 'Last Name is required',
              }}
            />

            <div className="flex-gap-16">
              <InputMain
                name="firstName"
                placeholder="Ex. Sarah"
                labelHeader="First Name"
                control={control}
                rules={{
                  required: 'First Name is required',
                }}
              />

              <InputMain name="middleName" labelHeader="Middle Name" placeholder="Ex. SS" control={control} />
            </div>

            <div className="flex-gap-16">
              <DateCalendarForm
                name="dob"
                label="DOB"
                control={control}
                rules={{
                  required: 'DOB is required',
                }}
              />

              <SelectMain
                list={SexTypesList}
                renderLabel={(option) => option?.name}
                name="sex"
                control={control}
                placeholder="Select Option"
                labelHeader="Sex"
                selectedValue={'id'}
                rules={{
                  required: 'Sex is required',
                }}
              />
            </div>
            <div className="flex-gap-16">
              <NumericFormatCustom
                name="weight"
                label="Weight"
                placeholder="0 bs"
                fixedDecimalScale={true}
                decimalScale={2}
                control={control}
              />

              <NumericFormatCustom
                name="height"
                label="Height"
                placeholder="0 in feet/inches"
                fixedDecimalScale={true}
                decimalScale={2}
                control={control}
              />
            </div>

            <InputMain
              name="mrnNumber"
              placeholder="Ex.14896357"
              labelHeader="MRN"
              control={control}
              rules={{
                required: 'MRN is required',
              }}
            />
            <div className="flex-gap-16">
              <PhoneInput2
                label="Phone Number*"
                placeholder={'ex (000) 000-0000'}
                name="phone"
                control={control}
                labelHeader="Phone Number"
                rules={{
                  required: 'Phone number is required',
                  minLength: {
                    value: 10,
                    message: 'Please enter a valid 10 digit number.',
                  },
                  validate: (value) => {
                    return value
                      ? value?.replace(/[^0-9]/g, '').length === 10 || 'Please enter a valid phone number.'
                      : true;
                  },
                }}
              />

              <PhoneInput2
                label="Secondary Phone"
                placeholder={'ex (000) 000-0000'}
                name="secondaryPhone"
                control={control}
                rules={{
                  minLength: {
                    value: 10,
                    message: 'Please enter a valid 10 digit number.',
                  },
                  validate: (value) => {
                    return value
                      ? value?.replace(/[^0-9]/g, '').length === 10 || 'Please enter a valid phone number.'
                      : true;
                  },
                }}
              />
            </div>
            <InputMain
              name="subject"
              placeholder="Enter patient note or short description"
              labelHeader="Subject"
              control={control}
            />

            <SelectMain
              list={PatientTypesList}
              renderLabel={(option) => option?.name}
              name="status"
              control={control}
              placeholder="Select a type"
              labelHeader="Patient Status"
              selectedValue={'id'}
              rules={{
                required: 'Patient status is required',
              }}
            />
          </div>
          <div style={{ width: '100%' }}>
            <SelectMain
              list={payersList?.payers}
              renderLabel={(option) => renderPayerName(option)}
              name="insurance"
              control={control}
              placeholder="Select Payer"
              labelHeader="Insurance"
              loadType="GET_PAYERS"
              selectedValue={'id'}
              rules={{
                required: 'Payer status is required',
              }}
            />

            <SelectMain
              list={RateTypesList}
              renderLabel={(option) => option?.name}
              name="los"
              control={control}
              placeholder="Select Option"
              labelHeader="Level of Service"
              selectedValue={'id'}
              rules={{
                required: 'Level of service is required',
              }}
            />

            <AddressInput control={control} allValues={getValues()?.address} setValue={setValue} required />
          </div>
        </div>
      </div>

      <CreateCancelButtons createText={info ? 'Save' : 'Add'} loading={!!loading?.length} />
    </form>
  );
};
